<template>
  <v-container fluid class="login-container">
    <v-row justify="center" class="pt-4 pt-sm-8">
      <v-col cols="12" sm="8" md="6" lg="5" xl="4" class="px-4 px-sm-0">
        <v-card class="login-card mx-auto">
          <v-card-title class="text-center pb-0 px-2">
            <div class="login-logo mb-4">
              <TenantLogo />
            </div>
            <h2 class="text-h5 text-sm-h4 font-weight-bold pb-2">Welkom</h2>
          </v-card-title>

          <v-card-text class="text-center pt-2 px-3 px-sm-6">
            <p class="text-body-2 text-sm-body-1 mb-4 mb-sm-6">
              Log in om toegang te krijgen tot het
              {{ tenantStore.tenantDisplayName }} platform
            </p>

            <v-btn
              :color="tenantStore.tenantPrimaryColor"
              size="large"
              block
              :loading="loading"
              prepend-icon="mdi-microsoft"
              class="mb-4 login-btn"
              @click="signInSSO"
            >
              <span class="btn-text">Inloggen met Microsoft</span>
            </v-btn>

            <!-- Reset button for MSAL issues -->
            <v-btn
              v-if="showResetButton"
              color="warning"
              size="large"
              block
              :loading="resetting"
              prepend-icon="mdi-refresh"
              class="mb-4 reset-btn"
              @click="resetMsalSession"
            >
              <span class="btn-text">Reset inlogsessie</span>
            </v-btn>

            <div
              v-if="status && status !== 'nothing happened yet'"
              class="status-message mt-4"
            >
              <v-alert type="info" density="compact" class="text-left">
                {{ status }}
              </v-alert>
            </div>
          </v-card-text>

          <v-card-text class="text-center text-caption text-grey pt-0 px-3">
            &copy; {{ new Date().getFullYear() }} {{ tenantStore.tenantName }}
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>
<script>
import { useAuthStoreRefactor } from "@/stores/authStoreRefactor";
import { useTenantStore } from "@/stores/tenantStore";
import { useRouter } from "vue-router";
import {
  getMsalInstance,
  getMsalToken,
  resetMsalState,
} from "../../services/msalService";
import TenantLogo from "@/components/TenantLogo.vue";

export default {
  components: {
    TenantLogo,
  },
  setup() {
    const authStore = useAuthStoreRefactor();
    const tenantStore = useTenantStore();
    const router = useRouter();
    return { authStore, tenantStore, router };
  },
  data() {
    return {
      status: "nothing happened yet",
      account: "",
      loading: false,
      showResetButton: false,
      resetting: false,
    };
  },
  async mounted() {
    try {
      console.log("LoginForm: mounted() called");
      console.log("LoginForm: Current URL:", window.location.href);
      console.log(
        "LoginForm: localStorage token:",
        localStorage.getItem("token"),
      );

      // Check if we're coming from a logout - if so, skip automatic login processing
      const urlParams = new URLSearchParams(window.location.search);
      const isFromLogout = urlParams.get("logout") === "true";

      console.log(
        "LoginForm: URL params:",
        Object.fromEntries(urlParams.entries()),
      );
      console.log("LoginForm: Is from logout:", isFromLogout);

      if (isFromLogout) {
        console.log(
          "LoginForm: Coming from logout, skipping automatic MSAL processing",
        );

        // Immediately clear any remaining MSAL accounts to prevent auto-login
        const msalInstance = getMsalInstance();
        const existingAccounts = msalInstance.getAllAccounts();

        if (existingAccounts.length > 0) {
          console.log(
            "LoginForm: Found residual MSAL accounts after logout, clearing them:",
            existingAccounts.length,
          );

          // Clear browser storage manually to remove any remaining MSAL state
          const keysToRemove = [];
          for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (key && (key.includes("msal") || key.includes("azure"))) {
              keysToRemove.push(key);
            }
          }
          keysToRemove.forEach((key) => sessionStorage.removeItem(key));
          console.log("LoginForm: Cleared residual MSAL session storage");
        }

        this.status = "Uitgelogd - klik op de knop om opnieuw in te loggen";

        // Clear the logout parameter from URL without page reload
        const url = new URL(window.location);
        url.searchParams.delete("logout");
        window.history.replaceState({}, "", url);
        console.log("LoginForm: Cleared logout parameter from URL");

        return; // Skip MSAL redirect promise handling
      }

      console.log("LoginForm: Proceeding with MSAL redirect promise handling");
      const msalInstance = getMsalInstance();

      // Check MSAL accounts before handling redirect
      const existingAccounts = msalInstance.getAllAccounts();
      console.log(
        "LoginForm: Existing MSAL accounts:",
        existingAccounts.length,
      );

      const response = await msalInstance.handleRedirectPromise();
      console.log("LoginForm: handleRedirectPromise response:", response);

      if (response) {
        this.status = "mounted check";
        this.account = response.account;
        console.log("LoginForm: Found account in response:", this.account);

        if (this.account) {
          this.status = "account found";
          console.log("LoginForm: Processing account login");

          try {
            this.loading = true;
            this.status = "getting token from Azure";
            const accessToken = await getMsalToken(this.account);
            console.log("LoginForm: Got access token, logging into API");
            await this.loginOnAPI(accessToken);
          } catch (err) {
            this.status = "Token ophalen mislukt. Probeer opnieuw.";
            console.error("Error getting MSAL token:", err);
          } finally {
            this.loading = false;
          }
        }
      } else {
        console.log(
          "LoginForm: No response from handleRedirectPromise, checking for existing accounts",
        );

        // Check if there are any existing accounts that might auto-login
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          console.log(
            "LoginForm: Found existing MSAL accounts, this might cause auto-login:",
            accounts,
          );
        } else {
          console.log("LoginForm: No existing MSAL accounts found");
        }
      }
    } catch (error) {
      this.status =
        "Er is een probleem opgetreden. Ververs de pagina en probeer opnieuw.";
      this.loading = false;
      console.error("LoginForm: Error in mounted():", error);
    }
  },
  methods: {
    async signInSSO() {
      this.status = "Inloggen gestart...";
      this.loading = true;
      try {
        const msalInstance = getMsalInstance();

        console.log("LoginForm: signInSSO called");
        console.log(
          "LoginForm: MSAL accounts before login:",
          msalInstance.getAllAccounts().length,
        );
        console.log(
          "LoginForm: Active account:",
          msalInstance.getActiveAccount(),
        );

        // Check for any ongoing interactions
        try {
          const interactionStatus = await msalInstance.handleRedirectPromise();
          console.log(
            "LoginForm: Interaction status check:",
            interactionStatus,
          );
        } catch (interactionError) {
          console.log(
            "LoginForm: Interaction check error (this might be normal):",
            interactionError.message,
          );
        }

        console.log(
          "LoginForm: Starting interactive login with fresh authentication",
        );
        const loginRequest = {
          scopes: ["openid", "profile", "User.Read"],
          prompt: "login", // Force fresh authentication - requires username/password + 2FA for this app
        };

        await msalInstance.loginRedirect(loginRequest);
      } catch (error) {
        console.error("LoginForm: SSO login error:", error);

        // Handle specific interaction_in_progress error
        if (
          error.message &&
          error.message.includes("interaction_in_progress")
        ) {
          console.log(
            "LoginForm: Detected interaction_in_progress error, showing reset option",
          );
          this.status =
            "Er loopt al een inlogpoging. Gebruik de reset-knop hieronder om opnieuw te proberen.";
          this.showResetButton = true;
          this.loading = false;
        } else {
          this.status =
            "Inloggen mislukt. Probeer het opnieuw of ververs de pagina.";
          this.loading = false;
        }
      }
    },
    async loginOnAPI(token) {
      this.status = "Verbinden met het platform...";
      this.loading = true;
      try {
        await this.authStore.loginSSO({ token });
        this.router.push("/");
      } catch (error) {
        this.status = "Verbinding met platform mislukt. Probeer opnieuw.";
        this.loading = false;
      }
    },

    async resetMsalSession() {
      this.resetting = true;
      try {
        console.log("LoginForm: Manually resetting MSAL state");
        await resetMsalState();
        this.status = "Status gereset. Probeer opnieuw in te loggen.";
        this.showResetButton = false;
        this.loading = false;
      } catch (error) {
        console.error("LoginForm: Manual MSAL reset failed:", error);
        this.status = "Reset mislukt. Ververs de pagina en probeer opnieuw.";
        this.loading = false;
      } finally {
        this.resetting = false;
      }
    },
  },
};
</script>
<style scoped>
.login-container {
  background-color: #f5f5f5;
  padding: 0;
  min-height: 100vh;
  overflow-x: hidden;
}

.login-card {
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  padding: 16px;
  max-width: 380px;
  width: 100%;
}

@media (min-width: 600px) {
  .login-card {
    padding: 24px 16px;
    max-width: 420px;
  }
}

@media (min-width: 960px) {
  .login-card {
    max-width: 450px;
  }
}

.login-logo {
  max-width: 160px;
  width: 100%;
  height: auto;
  margin: 0 auto;
  display: flex;
  justify-content: center;
  align-items: center;
}

.login-logo :deep(.logo-image) {
  max-height: 60px;
  max-width: 160px;
}

@media (min-width: 600px) {
  .login-logo {
    max-width: 180px;
  }

  .login-logo :deep(.logo-image) {
    max-height: 70px;
    max-width: 180px;
  }
}

@media (min-width: 960px) {
  .login-logo {
    max-width: 200px;
  }

  .login-logo :deep(.logo-image) {
    max-height: 80px;
    max-width: 200px;
  }
}

.status-message {
  min-height: 60px;
}

.login-btn,
.reset-btn {
  width: 100%;
  max-width: 100%;
  min-height: 48px;
  word-wrap: break-word;
  white-space: normal;
}

.btn-text {
  font-size: 13px;
  line-height: 1.2;
  text-align: center;
  display: block;
}

@media (min-width: 600px) {
  .btn-text {
    font-size: 15px;
  }
}

@media (min-width: 960px) {
  .btn-text {
    font-size: 16px;
  }
}

/* Ensure no horizontal overflow */
* {
  box-sizing: border-box;
}

.v-card-text {
  word-wrap: break-word;
  overflow-wrap: break-word;
}

/* Add some breathing room at the bottom */
.login-container::after {
  content: "";
  display: block;
  height: 40px;
}
</style>
